import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { useClient } from '../../core/hooks/useClient';
import { useLinkedClientAccount } from '../../core/hooks/useLinkedClientAccount';
import { ClientForm } from './components/ClientForm';
import { ClientServicesTab } from './components/ClientServicesTab';
import { ClientDocumentsTab } from './components/ClientDocumentsTab';
import { ClientJiraTab } from './components/ClientJiraTab';
import { ClientGithubTab } from './components/ClientGithubTab';
import { ClientQuickBooksTab } from './components/ClientQuickBooksTab';
import { ClientStatus, platformIdFromName, type ProjectPlatform } from '../../core/models/Client';
import { resolveProjectContact } from '../../core/utils/projectContact';
import { compareTaskVersions, formatTaskVersion, parseTaskVersion } from '../../core/constants/taskVersionMilestone';

export const ClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { client, loading, error, updateClient } = useClient(clientId!);
  const linkedClientId = client?.clientId || client?.organizationId || null;
  const { clientAccount } = useLinkedClientAccount(linkedClientId);
  const contact = resolveProjectContact(client, clientAccount);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newVersionValue, setNewVersionValue] = useState('');
  const [versionBusy, setVersionBusy] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');
  const [platformBusy, setPlatformBusy] = useState(false);
  const [newTaskLabelValue, setNewTaskLabelValue] = useState('');
  const [taskLabelBusy, setTaskLabelBusy] = useState(false);
  const [fieldToggleBusy, setFieldToggleBusy] = useState(false);

  const taskVersions = Array.from(
    new Set((client?.taskVersions || []).map((v) => parseTaskVersion(v)).filter((v): v is string => v != null))
  ).sort(compareTaskVersions);

  const normalizeTaskLabel = (raw: string): string | null => {
    const t = raw.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!t || t.length > 80) return null;
    return t;
  };

  const taskLabels = Array.from(
    new Set(
      (client?.taskLabels || [])
        .map((l) => normalizeTaskLabel(String(l)))
        .filter((l): l is string => l != null)
    )
  ).sort((a, b) => a.localeCompare(b));

  const handleUpdate = async (data: any) => {
    try {
      await updateClient(data);
      setIsEditMode(false);
    } catch (err) {
      console.error('Failed to update client:', err);
    }
  };

  const handleAddVersion = async () => {
    const parsed = parseTaskVersion(newVersionValue);
    if (!parsed) {
      alert('Please enter a valid version (for example: 1, 1.2, 0.15.10).');
      return;
    }
    if (taskVersions.includes(parsed)) {
      setNewVersionValue('');
      return;
    }
    try {
      setVersionBusy(true);
      await updateClient({ taskVersions: [...taskVersions, parsed].sort(compareTaskVersions) });
      setNewVersionValue('');
    } catch (err) {
      console.error('Failed to add project version:', err);
      alert('Failed to add project version.');
    } finally {
      setVersionBusy(false);
    }
  };

  const platforms = client?.platforms || [];

  const savePlatforms = async (next: ProjectPlatform[]) => {
    try {
      setPlatformBusy(true);
      await updateClient({ platforms: next });
      setNewPlatformName('');
    } catch (err) {
      console.error('Failed to update project platforms:', err);
      alert('Failed to update project platforms.');
    } finally {
      setPlatformBusy(false);
    }
  };

  const handleAddPlatform = async () => {
    const name = newPlatformName.trim();
    const id = platformIdFromName(name);
    if (!id) return;
    if (platforms.some((p) => p.id === id)) {
      setNewPlatformName('');
      return;
    }
    await savePlatforms([...platforms, { id, name }]);
  };

  const handleRemoveVersion = async (versionToRemove: string) => {
    try {
      setVersionBusy(true);
      await updateClient({ taskVersions: taskVersions.filter((v) => v !== versionToRemove) });
    } catch (err) {
      console.error('Failed to remove project version:', err);
      alert('Failed to remove project version.');
    } finally {
      setVersionBusy(false);
    }
  };

  const handleAddTaskLabel = async () => {
    const parsed = normalizeTaskLabel(newTaskLabelValue);
    if (!parsed) {
      alert('Enter a short label (for example: bug, feature, docs).');
      return;
    }
    if (taskLabels.includes(parsed)) {
      setNewTaskLabelValue('');
      return;
    }
    try {
      setTaskLabelBusy(true);
      await updateClient({ taskLabels: [...taskLabels, parsed].sort((a, b) => a.localeCompare(b)) });
      setNewTaskLabelValue('');
    } catch (err) {
      console.error('Failed to add task label:', err);
      alert('Failed to add task label.');
    } finally {
      setTaskLabelBusy(false);
    }
  };

  const handleRemoveTaskLabel = async (labelToRemove: string) => {
    try {
      setTaskLabelBusy(true);
      await updateClient({ taskLabels: taskLabels.filter((l) => l !== labelToRemove) });
    } catch (err) {
      console.error('Failed to remove task label:', err);
      alert('Failed to remove task label.');
    } finally {
      setTaskLabelBusy(false);
    }
  };

  const githubFieldsEnabled = client?.taskFieldVisibility?.github !== false;
  const jiraFieldsEnabled = client?.taskFieldVisibility?.jira !== false;

  const handleTaskFieldToggle = async (field: 'github' | 'jira', enabled: boolean) => {
    if (!client) return;
    try {
      setFieldToggleBusy(true);
      await updateClient({
        taskFieldVisibility: {
          ...(client?.taskFieldVisibility || {}),
          [field]: enabled,
        },
      });
    } catch (err) {
      console.error(`Failed to update ${field} task field visibility:`, err);
      alert(`Failed to update ${field} task field visibility.`);
    } finally {
      setFieldToggleBusy(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading project details..." />;
  }

  if (error || !client) {
    return <ErrorState message="Failed to load project details" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/clients')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-primary">{client.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Project ID: {client.id}
            </p>
          </div>
        </div>
        {!isEditMode && (
          <Button onClick={() => setIsEditMode(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Details
          </Button>
        )}
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="jira">Jira Integration</TabsTrigger>
          <TabsTrigger value="github">GitHub</TabsTrigger>
          <TabsTrigger value="quickbooks">QuickBooks</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card>
            <CardContent className="pt-6">
              {isEditMode ? (
                <ClientForm
                  initialData={client}
                  onSubmit={handleUpdate}
                  onCancel={() => setIsEditMode(false)}
                />
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{client.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p
                        className={`font-medium ${
                          client.status === ClientStatus.ACTIVE
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {client.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Client Account</p>
                      <p className="font-medium">
                        {contact.clientAccountName || 'Unassigned'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Client Email</p>
                      <p className="font-medium">{contact.email || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Client Phone</p>
                      <p className="font-medium">{contact.phone || '—'}</p>
                    </div>
                    {contact.address && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-muted-foreground">Client Address</p>
                        <p className="font-medium">
                          {contact.address.street}
                          <br />
                          {contact.address.city}, {contact.address.state}{' '}
                          {contact.address.postalCode}
                          <br />
                          {contact.address.country}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium">Platforms</p>
                    <p className="text-xs text-muted-foreground">
                      The apps this project covers (for example Driver app, Builder app, Ops admin). Clients are asked
                      which one a request is for, and tasks can be tagged with one. A GitHub issue label with the same
                      id sets it automatically.
                    </p>
                    {platforms.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No platforms configured yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {platforms.map((platform) => (
                          <Badge key={platform.id} variant="outline" className="flex items-center gap-1">
                            {platform.name}
                            <span className="text-muted-foreground">({platform.id})</span>
                            <button
                              type="button"
                              onClick={() => savePlatforms(platforms.filter((p) => p.id !== platform.id))}
                              disabled={platformBusy}
                              aria-label={`Remove ${platform.name}`}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 max-w-sm">
                      <Input
                        placeholder="Add platform (e.g. Driver app)"
                        value={newPlatformName}
                        onChange={(event) => setNewPlatformName(event.target.value)}
                        disabled={platformBusy}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddPlatform}
                        disabled={platformBusy || !platformIdFromName(newPlatformName)}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium">Task Versions (Project-specific)</p>
                    {taskVersions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No versions configured yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {taskVersions.map((version) => (
                          <Badge key={version} variant="outline" className="flex items-center gap-1">
                            {formatTaskVersion(version)}
                            <button
                              type="button"
                              onClick={() => handleRemoveVersion(version)}
                              disabled={versionBusy}
                              aria-label={`Remove ${formatTaskVersion(version)}`}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 max-w-sm">
                      <Input
                        placeholder="Add version (e.g. 0.15.10)"
                        value={newVersionValue}
                        onChange={(event) => setNewVersionValue(event.target.value)}
                        disabled={versionBusy}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddVersion}
                        disabled={versionBusy || !newVersionValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium">Task Labels (Project-specific)</p>
                    <p className="text-xs text-muted-foreground">
                      Suggested defaults (bug, feature) appear in the task dialog even if not listed here; add any custom
                      labels you want stored on this project.
                    </p>
                    {taskLabels.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No custom labels saved yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {taskLabels.map((label) => (
                          <Badge key={label} variant="outline" className="flex items-center gap-1 capitalize">
                            {label}
                            <button
                              type="button"
                              onClick={() => handleRemoveTaskLabel(label)}
                              disabled={taskLabelBusy}
                              aria-label={`Remove ${label}`}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 max-w-sm">
                      <Input
                        placeholder="Add label (e.g. bug, feature, docs)"
                        value={newTaskLabelValue}
                        onChange={(event) => setNewTaskLabelValue(event.target.value)}
                        disabled={taskLabelBusy}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddTaskLabel}
                        disabled={taskLabelBusy || !newTaskLabelValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium">Task Modal Sections</p>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={githubFieldsEnabled}
                        onChange={(event) => handleTaskFieldToggle('github', event.target.checked)}
                        disabled={fieldToggleBusy}
                      />
                      <span className="text-sm">Show GitHub fields on tasks</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={jiraFieldsEnabled}
                        onChange={(event) => handleTaskFieldToggle('jira', event.target.checked)}
                        disabled={fieldToggleBusy}
                      />
                      <span className="text-sm">Show Jira fields on tasks</span>
                    </label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services">
          <ClientServicesTab clientId={client.id} />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <ClientDocumentsTab clientId={client.id} />
        </TabsContent>

        {/* Jira Tab */}
        <TabsContent value="jira">
          <ClientJiraTab 
            clientId={client.id} 
            currentProjectId={client.jiraProjectId}
          />
        </TabsContent>

        {/* GitHub Tab */}
        <TabsContent value="github">
          <ClientGithubTab
            clientId={client.id}
            currentRepoId={client.githubRepoId}
            currentRepoOwner={client.githubRepoOwner}
            currentRepoName={client.githubRepoName}
            currentInstallationId={client.githubInstallationId}
            currentSync={client.githubSync}
          />
        </TabsContent>

        {/* QuickBooks Tab */}
        <TabsContent value="quickbooks">
          <ClientQuickBooksTab 
            clientId={client.id}
            currentCompanyId={client.quickbooksCompanyId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
